/**
 * RPC Client for HIVE-based blockchains
 * Adapted from the Tampermonkey script with enhancements for batch operations
 */

import { ChainAccount, FollowRelationship, HivePost } from '../types/index';

export interface RPCError extends Error {
  chain: string;
  node: string;
}

export class RPCClient {
  private cache = new Map<string, unknown>();
  private inflight = new Map<string, Promise<unknown>>();
  private timeout = 12000;

  constructor(
    private nodes: string[],
    private chain: 'hive' | 'steemit' | 'blurt'
  ) {}

  /**
   * Try multiple RPC payload styles to handle different implementations
   */
  private async rpcMulti(
    method: string,
    params: unknown[]
  ): Promise<unknown> {
    const payloads = [
      // JSON-RPC 2.0 standard
      { jsonrpc: '2.0', id: 1, method, params },
      // Legacy Appbase/condenser_api style
      { jsonrpc: '2.0', id: 2, method: 'call', params: ['condenser_api', method.split('.')[1], params] },
    ];

    const errors: string[] = [];

    for (const node of this.nodes) {
      for (let payloadIdx = 0; payloadIdx < payloads.length; payloadIdx++) {
        const payload = payloads[payloadIdx];
        try {
          const response = await this.fetchWithTimeout(node, payload);
          return response;
        } catch (error) {
          const errMsg = error instanceof Error ? error.message : String(error);
          errors.push(`${node} payload ${payloadIdx + 1}: ${errMsg}`);
          continue;
        }
      }
    }

    const fullError = `All attempts failed for ${method}:\n` + errors.join('\n');
    console.error(`[${this.chain}] ${fullError}`);
    
    const err = new Error(fullError) as RPCError;
    (err as any).chain = this.chain;
    (err as any).node = this.nodes[0];
    throw err;
  }

  private async fetchWithTimeout(node: string, payload: unknown): Promise<unknown> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.timeout);

    try {
      const response = await fetch(node, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        signal: controller.signal,
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const text = await response.text();
      let json;
      
      try {
        json = JSON.parse(text);
      } catch (parseError) {
        console.error(`[${this.chain}] JSON Parse Error from ${node}:`, text);
        throw new Error(`Invalid JSON response: ${text.substring(0, 100)}`);
      }

      if (json.error) {
        throw new Error(`RPC Error: ${JSON.stringify(json.error)}`);
      }

      if (json.result === undefined) {
        throw new Error('No result in RPC response');
      }

      return json.result;
    } catch (error) {
      throw error;
    } finally {
      clearTimeout(timeoutId);
    }
  }

  private getCacheKey(method: string, params: unknown[]): string {
    return `${this.chain}:${method}:${JSON.stringify(params)}`;
  }

  private toHivePost(raw: any): HivePost | null {
    if (!raw || !raw.author || !raw.permlink) {
      return null;
    }

    return {
      author: raw.author,
      title: raw.title || '',
      permlink: raw.permlink,
      created: raw.created,
      url: `https://hive.blog/@${raw.author}/${raw.permlink}`,
    };
  }

  async get_accounts(usernames: string[]): Promise<ChainAccount[]> {
    const cacheKey = this.getCacheKey('get_accounts', [usernames]);

    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey) as ChainAccount[];
    }

    if (this.inflight.has(cacheKey)) {
      return this.inflight.get(cacheKey) as Promise<ChainAccount[]>;
    }

    const promise = this.rpcMulti('condenser_api.get_accounts', [usernames]);

    this.inflight.set(cacheKey, promise);

    try {
      const result = (await promise) as ChainAccount[];
      this.cache.set(cacheKey, result);
      return result;
    } finally {
      this.inflight.delete(cacheKey);
    }
  }

  async get_following(
    username: string,
    startFollowing: string = '',
    followType: 'blog' | 'ignore' = 'blog',
    limit: number = 100
  ): Promise<FollowRelationship[]> {
    const cacheKey = this.getCacheKey('get_following', [
      username,
      startFollowing,
      followType,
      limit,
    ]);

    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey) as FollowRelationship[];
    }

    if (this.inflight.has(cacheKey)) {
      return this.inflight.get(cacheKey) as Promise<FollowRelationship[]>;
    }

    const promise = this.rpcMulti('condenser_api.get_following', [
      username,
      startFollowing,
      followType,
      limit,
    ]);

    this.inflight.set(cacheKey, promise);

    try {
      const result = (await promise) as FollowRelationship[];
      this.cache.set(cacheKey, result);
      return result;
    } finally {
      this.inflight.delete(cacheKey);
    }
  }

  async get_content(author: string, permlink: string): Promise<Partial<HivePost> | null> {
    const cacheKey = this.getCacheKey('get_content', [author, permlink]);

    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey) as Partial<HivePost> | null;
    }

    try {
      const result = (await this.rpcMulti('condenser_api.get_content', [author, permlink])) as any;
      
      if (!result || !result.author) {
        this.cache.set(cacheKey, null);
        return null;
      }

      const post: Partial<HivePost> = {
        author: result.author,
        title: result.title,
        permlink: result.permlink,
        created: result.created,
      };

      this.cache.set(cacheKey, post);
      return post;
    } catch {
      this.cache.set(cacheKey, null);
      return null;
    }
  }

  async get_discussions_by_created(
    tag: string = '',
    limit: number = 20,
    cursor?: { startAuthor?: string; startPermlink?: string }
  ): Promise<HivePost[]> {
    const cacheKey = this.getCacheKey('get_discussions_by_created', [tag, limit, cursor]);

    if (this.cache.has(cacheKey)) {
      console.log(`[${this.chain}] Returning cached discussions`);
      return this.cache.get(cacheKey) as HivePost[];
    }

    try {
      console.log(
        `[${this.chain}] Fetching discussions: tag="${tag}", limit=${limit}, start_author="${
          cursor?.startAuthor || ''
        }", start_permlink="${cursor?.startPermlink || ''}"`
      );
      const query: Record<string, unknown> = { tag, limit };
      if (cursor?.startAuthor && cursor?.startPermlink) {
        query.start_author = cursor.startAuthor;
        query.start_permlink = cursor.startPermlink;
      }

      const result = (await this.rpcMulti('condenser_api.get_discussions_by_created', [
        query,
      ])) as any[];

      if (!Array.isArray(result)) {
        console.error(`[${this.chain}] Expected array result, got:`, result);
        throw new Error('Invalid result format: expected array');
      }

      console.log(`[${this.chain}] Received ${result.length} posts`);

      const posts: HivePost[] = result
        .map((post) => this.toHivePost(post))
        .filter((post): post is HivePost => post !== null);

      console.log(`[${this.chain}] Filtered to ${posts.length} valid posts`);
      this.cache.set(cacheKey, posts);
      return posts;
    } catch (error) {
      console.error(`[${this.chain}] Failed to fetch discussions:`, error);
      return [];
    }
  }

  clearCache(): void {
    this.cache.clear();
  }
}

// Export singleton instances
const HIVE_NODES = ['https://api.hive.blog'];
const STEEM_NODES = ['https://api.steemit.com', 'https://api.justyy.com'];
const BLURT_NODES = [
  'https://rpc.blurt.world',
  'https://blurt-rpc.saboin.com',
];

export const hiveClient = new RPCClient(HIVE_NODES, 'hive');
export const steemitClient = new RPCClient(STEEM_NODES, 'steemit');
export const blurtClient = new RPCClient(BLURT_NODES, 'blurt');
